import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, Pencil, Sparkles, Check, X, Send, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * AiAnswerCard — CAATF Learning Layer feedback surface.
 *
 * Wraps any AI-generated clinical output with:
 *   • "N knowledge entries applied" chip (RAG provenance) when retrievedEntryIds is populated
 *   • Thumbs 👍/👎 quick feedback (posts to /api/learning/feedback)
 *   • "Improve this answer" button → structured correction modal
 *
 * Correction modal fields (green-lit Feb 2026):
 *   • corrected_answer  (required)
 *   • source_reference  (optional, e.g. "UpToDate 2024", "ACC/AHA §5.2")
 *   • domain_tag        (optional select, e.g. "charting", "cardiology", "red_flags")
 *
 * Props:
 *   platformModule: string — required, e.g. "charting" | "documentation-pro" | "eod" | "prior-auth" | "cardiology"
 *   originalQuery:  string — the prompt/question that produced the output
 *   originalAiResponse: string — the AI output the physician is reacting to
 *   retrievedEntryIds: string[] — RAG hit ids used to draft this response (may be empty)
 *   testid:  string — component root data-testid prefix (default "ai-answer-card")
 *   suggestedDomain: string — optional default for the domain select
 */
const DOMAINS = [
  { value: "", label: "— none —" },
  { value: "charting", label: "Charting / SOAP" },
  { value: "documentation", label: "Documentation" },
  { value: "prior_auth", label: "Prior Auth" },
  { value: "cardiology", label: "Cardiology" },
  { value: "eod", label: "End-of-Day" },
  { value: "red_flags", label: "Red Flags (safety)" },
  { value: "medication_considerations", label: "Medication Considerations (safety)" },
  { value: "billing", label: "Billing / Claims" },
  { value: "referrals", label: "Referrals" },
];

export default function AiAnswerCard({
  platformModule,
  originalQuery = "",
  originalAiResponse = "",
  retrievedEntryIds = [],
  suggestedDomain = "",
  testid = "ai-answer-card",
}) {
  const [rating, setRating] = useState(null);           // "up" | "down" | null
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [correction, setCorrection] = useState("");
  const [source, setSource] = useState("");
  const [domain, setDomain] = useState(suggestedDomain);

  const kbCount = Array.isArray(retrievedEntryIds) ? retrievedEntryIds.length : 0;

  const submitFeedback = async ({ rating, correction_text, correct_approach_text }) => {
    setSubmitting(true);
    try {
      await api.post("/learning/feedback", {
        platform_module: platformModule,
        original_query: (originalQuery || "").slice(0, 4000),
        original_ai_response: (originalAiResponse || "").slice(0, 8000),
        retrieved_entry_ids: retrievedEntryIds,
        rating,
        correction_text: correction_text || null,
        correct_approach_text: correct_approach_text || null,
        confidence_level: correction_text ? "certain" : null,
      });
      setSent(true);
      setModalOpen(false);
      toast.success("Thanks — feedback logged to the learning layer.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const onThumb = (value) => {
    if (sent || submitting) return;
    setRating(value);
    if (value === "up") {
      submitFeedback({ rating: "up" });
    } else {
      // down → open the correction modal for structured input
      setModalOpen(true);
    }
  };

  const onSubmitCorrection = () => {
    if (!correction.trim()) {
      toast.error("Please describe the correct answer.");
      return;
    }
    const merged = source.trim()
      ? `${correction.trim()}\n\nSource: ${source.trim()}`
      : correction.trim();
    // Domain hint travels as a bracket-prefix on correct_approach_text; admin
    // uses it as a default when promoting.  Backend does not require it.
    const approach = domain
      ? `[domain:${domain}] ${merged}`
      : merged;
    submitFeedback({
      rating: rating || "down",
      correction_text: correction.trim(),
      correct_approach_text: approach,
    });
  };

  if (sent) {
    return (
      <div
        data-testid={`${testid}-sent`}
        className="mt-3 border-t border-brand pt-3 flex items-center gap-2 text-xs text-emerald-700"
      >
        <Check className="w-3.5 h-3.5" />
        Feedback logged — the learning layer will consider your input.
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-brand pt-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.14em] text-brand-muted">
            Improve this answer
          </div>
          {kbCount > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 border border-violet-200 text-violet-700"
              title={`RAG retrieved ${kbCount} knowledge entries to draft this output`}
              data-testid={`${testid}-kb-chip`}
            >
              <BookOpen className="w-3 h-3" />
              {kbCount} knowledge {kbCount === 1 ? "entry" : "entries"} applied
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onThumb("up")}
            disabled={submitting}
            className={`px-2.5 py-1.5 rounded-md border text-xs inline-flex items-center gap-1.5 transition-colors disabled:opacity-60 ${
              rating === "up"
                ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                : "border-brand text-brand-muted hover:bg-brand-bg"
            }`}
            data-testid={`${testid}-thumbs-up`}
          >
            <ThumbsUp className="w-3.5 h-3.5" /> Helpful
          </button>
          <button
            onClick={() => onThumb("down")}
            disabled={submitting}
            className={`px-2.5 py-1.5 rounded-md border text-xs inline-flex items-center gap-1.5 transition-colors disabled:opacity-60 ${
              rating === "down"
                ? "bg-red-50 border-red-300 text-red-700"
                : "border-brand text-brand-muted hover:bg-brand-bg"
            }`}
            data-testid={`${testid}-thumbs-down`}
          >
            <ThumbsDown className="w-3.5 h-3.5" /> Not quite
          </button>
          <button
            onClick={() => { setRating("down"); setModalOpen(true); }}
            disabled={submitting}
            className="px-2.5 py-1.5 rounded-md border border-brand-primary text-brand-primary text-xs inline-flex items-center gap-1.5 hover:bg-brand-primary hover:text-white transition-colors disabled:opacity-60"
            data-testid={`${testid}-improve-btn`}
          >
            <Pencil className="w-3.5 h-3.5" /> Improve this answer
          </button>
        </div>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
          onClick={() => !submitting && setModalOpen(false)}
          data-testid={`${testid}-modal`}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
              data-testid={`${testid}-modal-close`}
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-brand-primary" />
              <div className="text-base font-semibold text-slate-900">
                Improve this AI answer
              </div>
            </div>
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">
              Your correction is logged to the CAATF learning layer. An admin will
              review it before it becomes part of the shared knowledge base.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Corrected answer <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={correction}
                  onChange={(e) => setCorrection(e.target.value)}
                  rows={4}
                  maxLength={4000}
                  placeholder="What SHOULD the answer say? Be specific — this text may be promoted verbatim to the KB."
                  className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                  data-testid={`${testid}-corrected-answer`}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Source / reference <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  maxLength={200}
                  placeholder="e.g. UpToDate 2024, ACC/AHA §5.2, personal case notes"
                  className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                  data-testid={`${testid}-source-reference`}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Domain tag <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <select
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                  data-testid={`${testid}-domain-tag`}
                >
                  {DOMAINS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button
                onClick={() => setModalOpen(false)}
                disabled={submitting}
                className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg disabled:opacity-60"
                data-testid={`${testid}-cancel`}
              >
                Cancel
              </button>
              <button
                onClick={onSubmitCorrection}
                disabled={submitting || !correction.trim()}
                className="text-xs px-3 py-1.5 rounded-md bg-brand-primary text-white hover:bg-[#35096B] inline-flex items-center gap-1.5 disabled:opacity-60"
                data-testid={`${testid}-submit`}
              >
                <Send className="w-3.5 h-3.5" />
                {submitting ? "Sending…" : "Submit correction"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
